import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import {
    ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { forecastAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const SITE_COLORS = {
    'FE Génipa': '#10b981', 'FE Cluny': '#3b82f6', 'FE Dillon': '#f59e0b',
    'FE Océanis': '#8b5cf6', 'FE Vauclin': '#ec4899',
};
const FACTORS = [
    { key: 'vacances', label: 'Vacances scolaires' },
    { key: 'feries', label: 'Fériés (J-1 / J / J+1)' },
    { key: 'paie', label: 'Cycle de paie' },
];
const PAY_BUCKETS = ['1-7 (CAF le 5)', '8-14', '15-21', '22-fin'];
const DOW = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

const cellMult = (c, factors) =>
    c.cw * (factors.vacances ? c.cv : 1) * (factors.feries ? c.ch : 1) * (factors.paie ? c.cp : 1);

export default function ForecastSection() {
    const { user, hasPermission } = useAuth();
    const allowedIds = user?.allowed_sites?.length ? user.allowed_sites : null;
    const [open, setOpen] = useState(() => localStorage.getItem('forecast_section_open') === '1');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [recalcing, setRecalcing] = useState(false);
    const [factors, setFactors] = useState({ vacances: true, feries: true, paie: true });
    const [sel, setSel] = useState(null); // {siteName: bool}

    const toggleOpen = () => setOpen(v => {
        localStorage.setItem('forecast_section_open', v ? '0' : '1');
        return !v;
    });

    const load = async () => {
        setLoading(true);
        try {
            const r = await forecastAPI.ca({ days: 30, actual_days: 14 });
            setData(r.data);
            setSel(prev => prev || Object.fromEntries(r.data.sites.map(s => [s.name, true])));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (open && !data) load(); /* eslint-disable-next-line */ }, [open]);

    const siteList = useMemo(
        () => (data?.sites || []).filter(s => !allowedIds || allowedIds.includes(s.site_id)),
        [data, allowedIds]
    );

    const rows = useMemo(() => {
        if (!data || !sel) return [];
        const names = siteList.filter(s => sel[s.name]).map(s => s.name);
        const out = [];
        for (const a of data.actuals) {
            let sum = 0, has = false;
            for (const n of names) if (a.sites[n] != null) { sum += a.sites[n]; has = true; }
            out.push({ date: a.date, actual: has ? Math.round(sum) : null });
        }
        const lastActual = [...out].reverse().find(r => r.actual != null);
        for (const f of data.forecast) {
            let pred = 0, lo = 0, hi = 0;
            for (const n of names) {
                const c = f.sites[n];
                if (!c || c.closed) continue;
                const p = c.base * cellMult(c, factors);
                pred += p; lo += p * c.low; hi += p * c.high;
            }
            out.push({
                date: f.date, pred: Math.round(pred), band: [Math.round(lo), Math.round(hi)],
                _ctx: f, _names: names,
            });
        }
        if (lastActual) lastActual.pred = lastActual.actual; // raccord visuel réel → prévu
        return out;
    }, [data, sel, factors, siteList]);

    const recalc = async () => {
        setRecalcing(true);
        try {
            await forecastAPI.recalculate();
            await load();
        } finally {
            setRecalcing(false);
        }
    };

    return (
        <Card data-testid="forecast-section">
            <CardHeader className={`py-2 px-3 ${open ? 'border-b' : ''} space-y-2`}>
                <div className="flex flex-wrap items-center gap-2">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    <CardTitle className="text-sm">Prédictif CA — 30 jours</CardTitle>
                    {open && data && (
                        <Badge variant="outline" className="text-[10px] font-mono text-slate-500" data-testid="forecast-computed-at">
                            calculé le {data.computed_at?.slice(0, 16).replace('T', ' ')}
                        </Badge>
                    )}
                    <div className="ml-auto flex items-center gap-1.5">
                        {open && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={recalc} disabled={recalcing} data-testid="forecast-recalc-btn">
                                {recalcing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                                Recalculer
                            </Button>
                        )}
                        <button
                            type="button"
                            onClick={toggleOpen}
                            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 rounded h-7 px-2 flex items-center gap-1"
                            data-testid="forecast-section-toggle"
                            title={open ? 'Masquer le prédictif CA' : 'Afficher le prédictif CA'}
                        >
                            {open ? '▼' : '▶'} {open ? 'Masquer' : 'Afficher'}
                        </button>
                    </div>
                </div>
                {open && data && (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase text-slate-400 font-semibold">Ressources :</span>
                    {FACTORS.map(f => (
                        <label key={f.key}
                            className={`inline-flex items-center gap-1 px-2 h-6 rounded-md border cursor-pointer text-[11px] font-semibold transition-colors ${factors[f.key] ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-400'}`}
                            data-testid={`forecast-factor-${f.key}`}>
                            <Checkbox checked={factors[f.key]} onCheckedChange={() => setFactors(v => ({ ...v, [f.key]: !v[f.key] }))} className="w-3 h-3" />
                            {f.label}
                        </label>
                    ))}
                    <span className="text-[10px] uppercase text-slate-400 font-semibold ml-2">Sites :</span>
                    {siteList.map(s => {
                        const checked = !!sel?.[s.name];
                        const color = SITE_COLORS[s.name] || '#64748b';
                        return (
                            <label key={s.name}
                                className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border cursor-pointer text-[11px] font-semibold ${checked ? 'bg-white' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                                style={{ borderColor: checked ? color : undefined, color: checked ? color : undefined }}>
                                <Checkbox checked={checked} onCheckedChange={() => setSel(v => ({ ...v, [s.name]: !v[s.name] }))} className="w-3 h-3"
                                    data-testid={`forecast-site-${s.name.replace('FE ', '').toLowerCase()}`} />
                                {s.name.replace('FE ', '')}
                            </label>
                        );
                    })}
                </div>
                )}
            </CardHeader>
            {open && (
            <CardContent className="p-2">
                {loading && !data ? (
                    <div className="h-40 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : !data ? null : (
                <>
                <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" interval="preserveStartEnd"
                            tick={(props) => {
                                const { x, y, payload } = props;
                                const d = new Date(payload.value + 'T00:00:00');
                                const wk = d.getDay() === 0 || d.getDay() === 6;
                                return (
                                    <g transform={`translate(${x},${y})`}>
                                        <text x={0} y={0} dy={12} textAnchor="middle" fontSize={10} fill="#64748b">
                                            {payload.value.slice(8, 10)}/{payload.value.slice(5, 7)}
                                        </text>
                                        <text x={0} y={0} dy={22} textAnchor="middle" fontSize={9}
                                            fill={wk ? '#f87171' : '#94a3b8'} fontWeight={wk ? 'bold' : 'normal'}>
                                            {['D', 'L', 'M', 'M', 'J', 'V', 'S'][d.getDay()]}
                                        </text>
                                    </g>
                                );
                            }} height={34} />
                        <YAxis tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} tick={{ fontSize: 11, fill: '#64748b' }} width={50} />
                        <Tooltip content={<ForecastTooltip factors={factors} />} />
                        <Area dataKey="band" stroke="none" fill="#6366f1" fillOpacity={0.12} isAnimationActive={false} connectNulls={false} name="Fourchette" />
                        <Line dataKey="actual" name="Réalisé" stroke="#0f172a" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} connectNulls={false} />
                        <Line dataKey="pred" name="Prévu" stroke="#6366f1" strokeWidth={2} strokeDasharray="5 4" dot={false} activeDot={{ r: 3 }} isAnimationActive={false} connectNulls={false} />
                    </ComposedChart>
                </ResponsiveContainer>
                <div className="text-[10px] text-slate-400 italic px-2">
                    Courbe pleine : CA réalisé (somme des sites cochés). Pointillé : prévision = CA jour type × coefficient jour de semaine × ressources cochées.
                    La bande s'élargit avec l'horizon (incertitude croissante). Survolez un jour pour le détail du calcul.
                </div>
                {hasPermission('sales_forecast_accuracy_view') && <AccuracyPanel />}
                </>
                )}
            </CardContent>
            )}
        </Card>
    );
}

function ForecastTooltip({ active, payload, label, factors }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    const d = new Date(label + 'T00:00:00');
    const ctx = row._ctx;
    return (
        <div className="bg-white border rounded-md shadow-lg p-2 text-[11px] max-w-[300px]">
            <div className="font-semibold text-slate-700 mb-1">{DOW[d.getDay()]} {label}{ctx ? ' — prévision' : ''}</div>
            {row.actual != null && (
                <div className="flex justify-between gap-3"><span>Réalisé</span><span className="font-semibold tabular-nums">{formatCurrency(row.actual)}</span></div>
            )}
            {ctx && (
                <>
                    <div className="flex justify-between gap-3 text-indigo-700"><span>Prévu</span><span className="font-semibold tabular-nums">{formatCurrency(row.pred)}</span></div>
                    {row.band && (
                        <div className="flex justify-between gap-3 text-slate-400"><span>Fourchette</span>
                            <span className="tabular-nums">{formatCurrency(row.band[0])} → {formatCurrency(row.band[1])}</span></div>
                    )}
                    <div className="mt-1 pt-1 border-t space-y-0.5 text-[10px] text-slate-500">
                        {ctx.vacation && <div>{factors.vacances ? '✓' : '✗'} Vacances : {ctx.vacation}</div>}
                        {ctx.holiday && <div>{factors.feries ? '✓' : '✗'} {ctx.holiday}</div>}
                        <div>{factors.paie ? '✓' : '✗'} Cycle de paie : {PAY_BUCKETS[ctx.pay_bucket]}</div>
                    </div>
                    <div className="mt-1 pt-1 border-t space-y-0.5">
                        {(row._names || []).map(n => {
                            const c = ctx.sites[n];
                            if (!c) return null;
                            const p = c.closed ? 0 : Math.round(c.base * cellMult(c, factors));
                            return (
                                <div key={n} className="flex justify-between gap-2">
                                    <span style={{ color: SITE_COLORS[n] }}>{n.replace('FE ', '')}{c.closed ? ' (fermé)' : ''}</span>
                                    <span className="tabular-nums text-slate-600">
                                        {c.closed ? '0 €' : `${c.base}€ ×${cellMult(c, factors).toFixed(2)} = ${formatCurrency(p)}`}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

function AccuracyPanel() {
    const [open, setOpen] = useState(false);
    const [weeks, setWeeks] = useState(null);
    const [loading, setLoading] = useState(false);

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && weeks === null) {
            setLoading(true);
            try {
                const r = await forecastAPI.accuracy({ weeks: 8 });
                setWeeks(r.data.weeks || []);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="mt-2 border-t pt-2 px-2" data-testid="forecast-accuracy-panel">
            <button type="button" onClick={toggle}
                className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                data-testid="forecast-accuracy-toggle">
                {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Suivi prévu vs réalisé (hebdomadaire)
            </button>
            {open && (
                loading ? <div className="py-3 text-center text-slate-400"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
                : !weeks?.length ? (
                    <div className="py-2 text-[11px] text-slate-400 italic">
                        Pas encore de données : les prévisions figées chaque nuit seront comparées au réalisé dès la semaine prochaine.
                    </div>
                ) : (
                    <table className="w-full text-[11px] mt-1">
                        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                            <tr>
                                <th className="text-left px-2 py-1">Semaine</th>
                                <th className="text-right px-2 py-1">Prévu</th>
                                <th className="text-right px-2 py-1">Réalisé</th>
                                <th className="text-right px-2 py-1">Écart</th>
                            </tr>
                        </thead>
                        <tbody>
                            {weeks.map(w => (
                                <tr key={w.start} className="border-b border-slate-100">
                                    <td className="px-2 py-1 font-mono text-slate-600">{w.week} ({w.start.slice(8, 10)}/{w.start.slice(5, 7)})</td>
                                    <td className="px-2 py-1 text-right tabular-nums">{formatCurrency(w.total.pred)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatCurrency(w.total.actual)}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums font-semibold ${Math.abs(w.total.err_pct || 0) <= 10 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                        {w.total.err_pct > 0 ? '+' : ''}{w.total.err_pct}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )
            )}
        </div>
    );
}
